import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import {
  Sparkles,
  Search,
  Bot,
  Compass,
  TrendingUp,
  Target,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
  Layers,
  Trophy,
  Users,
  Rocket,
  ListChecks,
  FileText,
} from "lucide-react";

const PAGE_TITLE =
  "uSeen Nasıl Çalışır? Yapay Zekâ Görünürlük Analizi | uSeen";
const PAGE_DESC =
  "uSeen, işletmenizin yapay zekâlarda ne kadar görünür olduğunu analiz eder, rakiplerle karşılaştırır, eksikleri belirler ve uygulanabilir görevler oluşturur.";

const FAQ_ITEMS = [
  {
    q: "uSeen nedir?",
    a: "uSeen, işletmelerin yapay zekâ destekli arama ve cevap sistemlerinde ne kadar görünür olduğunu analiz eden ve görünürlüğü artırmak için görevler oluşturan bir AI Visibility platformudur.",
  },
  {
    q: "uSeen sadece SEO aracı mı?",
    a: "Hayır. uSeen yalnızca klasik SEO'ya odaklanmaz. AI Visibility, GEO, AI arama niyeti kapsamı, web sitesi içerik kalitesi, rakip görünürlüğü ve görev bazlı iyileştirme alanlarını birlikte değerlendirir.",
  },
  {
    q: "uSeen hangi yapay zekâlar için görünürlük sağlar?",
    a: "uSeen; ChatGPT, Gemini, Google AI, Perplexity ve benzeri yapay zekâ destekli arama ve cevap sistemlerinde işletmenizin daha anlaşılır ve önerilebilir hale gelmesine yardımcı olur.",
  },
  {
    q: "uSeen işletmem için ne üretir?",
    a: "Analiz sonuçlarına göre eksikleri belirler, uygulanabilir görevler oluşturur ve görünürlüğü artırmak için içerik önerileri sunar.",
  },
  {
    q: "uSeen kimler için uygundur?",
    a: "Restoranlar, klinikler, güzellik merkezleri, oteller, kamp alanları, oto servisleri, emlak ofisleri, özel okullar ve yerel hizmet işletmeleri başta olmak üzere dijital görünürlüğünü artırmak isteyen tüm işletmeler için uygundur.",
  },
  {
    q: "uSeen kullanmak yapay zekâlarda kesin önerileceğim anlamına gelir mi?",
    a: "Hayır. uSeen herhangi bir yapay zekâ platformunda kesin önerilme garantisi vermez. uSeen'in amacı işletmenizin dijital ayak izlerini güçlendirmek, yapay zekâlar tarafından daha anlaşılır hale gelmesini sağlamak ve önerilere daha hazır bir görünürlük yapısı oluşturmaktır.",
  },
];

/**
 * Public SEO/sales page — /nasil-calisir
 * Mirrors AIVisibilityNedir + GeoNedir pattern: head meta override + FAQPage JSON-LD on mount,
 * restored on unmount. No auth, no backend.
 */
export default function NasilCalisir() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute("content", PAGE_DESC);

    const tags = [
      ["property", "og:title", PAGE_TITLE],
      ["property", "og:description", PAGE_DESC],
      ["property", "og:type", "article"],
      ["name", "twitter:title", PAGE_TITLE],
      ["name", "twitter:description", PAGE_DESC],
    ];
    const added = [];
    for (const [attr, key, val] of tags) {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute("content", val);
    }

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-useen-faq", "nasil-calisir");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== undefined && prevDesc !== null) {
        metaDesc.setAttribute("content", prevDesc);
      }
      added.forEach((t) => t.parentNode?.removeChild(t));
      ld.parentNode?.removeChild(ld);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col" data-testid="nasil-calisir-page">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">Fiyatlandırma</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">Giriş Yap</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium" data-testid="header-cta-register">
            Ücretsiz Başla
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> uSeen · Nasıl Çalışır?
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
            uSeen Nasıl Çalışır?
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-3">
            uSeen, işletmenizin yapay zekâ destekli arama ve cevap sistemlerinde
            <strong className="text-white"> ne kadar görünür, anlaşılır ve önerilebilir </strong>
            olduğunu analiz eden bir AI Visibility platformudur.
          </p>
          <p className="text-slate-400 leading-relaxed mb-3">
            Bugünün dijital dünyasında işletmeler yalnızca Google'da değil; ChatGPT, Gemini, Google AI,
            Perplexity ve benzeri yapay zekâ sistemlerinde de görünür olmak zorundadır.
          </p>
          <p className="text-slate-400 leading-relaxed mb-10">
            uSeen, işletmenizin bu yeni görünürlük alanında <em>nerede durduğunu</em> ölçer, eksikleri
            tespit eder ve görünürlüğünüzü artırmak için <em>uygulanabilir görevler</em> oluşturur.
          </p>

          <Article>
            <Section icon={<Target size={18} />} title="uSeen'in Temel Amacı Nedir?">
              <p>
                uSeen'in amacı, işletmenizin yapay zekâlar tarafından daha kolay bulunmasını, daha doğru
                anlaşılmasını ve daha fazla önerilebilir hale gelmesini sağlamaktır.
              </p>
              <p>
                Bunu yaparken yalnızca web sitenize bakmaz. İşletmenizin internetteki genel dijital
                varlığını, içerik yapısını, hizmet açıklamalarını, müşteri niyetlerini ve rakiplerinize
                göre konumunu değerlendirir.
              </p>
              <p>
                Çünkü yapay zekâlar bir işletmeyi değerlendirirken tek bir kaynağa göre karar vermez.
                Web sitesi, Google Business Profile, müşteri yorumları, sosyal medya hesapları, üçüncü
                parti kaynaklar ve internetteki bilgi tutarlılığı bir bütün olarak önemlidir.
              </p>
            </Section>

            <Section icon={<HelpCircle size={18} />} title="uSeen Hangi Sorulara Cevap Verir?">
              <p>uSeen şu soruların cevaplarını bulmanıza yardımcı olur:</p>
              <BulletList items={[
                "İşletmem yapay zekâlarda görünür mü?",
                "ChatGPT veya Google AI beni doğru anlayabilir mi?",
                "Rakiplerim benden daha mı görünür?",
                "Web sitemde hangi bilgiler eksik?",
                "Hangi müşteri niyetlerinde güçlü, hangilerinde zayıfım?",
                "Hangi içerikleri üretmem gerekiyor?",
                "Yapay zekâların beni önermesi için hangi eksikleri tamamlamalıyım?",
                "Dijital varlığımda tutarsız veya zayıf noktalar var mı?",
              ]} />
              <p>
                Bu soruların cevabı, işletmenizin
                <strong className="text-white"> yeni nesil arama dünyasına ne kadar hazır </strong>
                olduğunu gösterir.
              </p>
            </Section>

            <Section icon={<Layers size={18} />} title="uSeen Çalışma Süreci">
              <p>
                uSeen, karmaşık teknik analizleri işletme sahibinin anlayabileceği sade ve uygulanabilir
                bir sürece dönüştürür. Aşağıdaki adımlarla çalışır:
              </p>

              <Step n={1} title="İşletme Bilgileri Alınır">
                <p>İlk adımda işletmenize ait temel bilgiler alınır. Bunlar şunlar olabilir:</p>
                <BulletList items={[
                  "İşletme adı",
                  "Web sitesi",
                  "Hizmet verdiği sektör",
                  "Konum bilgisi",
                  "Hedef müşteri kitlesi",
                  "Ana hizmetler",
                  "Rakipler",
                  "Google Maps veya işletme profil bilgileri",
                  "İşletmenin hedeflediği müşteri soruları",
                ]} />
                <p className="text-sm text-slate-400">Bu bilgiler, analiz sürecinin temelini oluşturur.</p>
              </Step>

              <Step n={2} title="Dijital Varlık Analizi Yapılır">
                <p>uSeen, işletmenizin internette nasıl göründüğünü analiz eder. Bu aşamada şu alanlara bakılır:</p>
                <BulletList items={[
                  "Web sitenizin açıklayıcı olup olmadığı",
                  "Hizmetlerinizin net anlatılıp anlatılmadığı",
                  "Konum ve iletişim bilgilerinizin doğru olup olmadığı",
                  "Sitenizde SSS, hizmet, hakkımızda ve iletişim bölümlerinin yeterli olup olmadığı",
                  "Teknik görünürlük sorunları",
                  "Yapılandırılmış veri kullanımı",
                  "İçerik eksikleri",
                  "Rakiplere göre dijital hazırlık seviyesi",
                ]} />
                <p className="text-sm text-slate-400">
                  Amaç, yapay zekâların işletmenizi ne kadar doğru anlayabileceğini ölçmektir.
                </p>
              </Step>

              <Step n={3} title="AI Görünürlük Skoru Oluşturulur">
                <p>
                  uSeen, analiz sonucunda işletmeniz için bir <strong className="text-white">AI Hazırlık
                  ve Görünürlük Skoru</strong> üretir.
                </p>
                <p>
                  Bu skor, işletmenizin yapay zekâ destekli arama sistemleri tarafından ne kadar
                  anlaşılabilir, bulunabilir ve önerilebilir olduğunu gösteren özet bir göstergedir.
                </p>
                <p className="text-sm text-slate-400">
                  Skor; içerik gücü, hizmet netliği, lokasyon netliği, güven sinyalleri, teknik yapı
                  ve niyet kapsamı gibi farklı alanlardan beslenir.
                </p>
              </Step>

              <Step n={4} title="AI Arama Niyeti Kapsamı Analiz Edilir">
                <p>
                  uSeen'in en önemli alanlarından biri <strong className="text-white">AI arama niyeti
                  kapsamıdır.</strong>
                </p>
                <p>
                  Kullanıcılar yapay zekâlara yalnızca işletme adı sormaz. Genellikle ihtiyaç odaklı
                  sorular sorar:
                </p>
                <BulletList items={[
                  "Yakınımda iyi bir restoran var mı?",
                  "Tuzla'da güvenilir oto servis nerede?",
                  "Çocukla gidilecek otel önerir misin?",
                  "Acil diş tedavisi için hangi klinik uygun?",
                  "Uygun fiyatlı güzellik merkezi var mı?",
                ]} />
                <p>
                  uSeen, işletmenizin bu tür müşteri niyetlerinde ne kadar güçlü olduğunu analiz eder.
                  Hangi niyetlerde görünürlük potansiyeliniz güçlü, hangilerinde zayıf veya eksik
                  olduğunu gösterir.
                </p>
              </Step>

              <Step n={5} title="Rakip Karşılaştırması Yapılır">
                <p>
                  Görünürlük yalnızca sizin durumunuzla ilgili değildir. Rakiplerinizin sizden daha
                  güçlü olduğu alanları da görmek gerekir.
                </p>
                <p>uSeen, rakiplerle karşılaştırmalı analiz yaparak şu sorulara cevap arar:</p>
                <BulletList items={[
                  "Rakipler hangi konularda daha görünür?",
                  "Rakiplerin web sitesi daha mı açıklayıcı?",
                  "Rakiplerin hizmet açıklamaları daha mı güçlü?",
                  "Rakiplerde olup sizde olmayan içerikler var mı?",
                  "Rakipler yapay zekâlar tarafından daha kolay mı anlaşılır?",
                ]} />
                <p className="text-sm text-slate-400">
                  Bu analiz, işletmenizin hangi alanlarda geri kaldığını netleştirir.
                </p>
              </Step>

              <Step n={6} title="Eksikler Görevlere Dönüştürülür">
                <p>
                  uSeen'in en önemli farkı, yalnızca analiz yapmakla kalmamasıdır. Tespit edilen
                  eksikleri <strong className="text-white">uygulanabilir görevlere dönüştürür.</strong>
                </p>
                <p>Örneğin:</p>
                <BulletList items={[
                  "Web sitenize sık sorulan sorular bölümü ekleyin",
                  "Hizmet açıklamalarınızı daha net hale getirin",
                  "Google işletme açıklamanızı güçlendirin",
                  "Lokasyon ve bölge vurgunuzu artırın",
                  "Eksik müşteri niyetleri için içerik üretin",
                  "LocalBusiness schema ekleyin",
                  "Rakiplerde olup sizde olmayan hizmet açıklamalarını tamamlayın",
                  "Yorum isteme mesajı hazırlayın",
                ]} />
                <p className="text-sm text-slate-400">
                  Bu sayede işletme sahibi karmaşık bir raporla baş başa kalmaz. Ne yapması gerektiğini
                  adım adım görür.
                </p>
              </Step>

              <Step n={7} title="Hazır İçerik Önerileri Üretilir">
                <p>uSeen, görünürlüğü artırmak için gerekli içerik önerilerini de sunabilir. Örneğin:</p>
                <BulletList items={[
                  "Web sitesi için hizmet açıklamaları",
                  "Google Business Profile açıklaması",
                  "Sık sorulan sorular",
                  "Hakkımızda metni",
                  "Blog başlıkları",
                  "Meta açıklamaları",
                  "Yorum isteme mesajları",
                  "Instagram bio önerileri",
                  "LocalBusiness schema ve FAQ schema taslakları",
                ]} />
                <p className="text-sm text-slate-400">
                  Bu içerikler, işletmenin yapay zekâlar tarafından daha iyi anlaşılmasına yardımcı olur.
                </p>
              </Step>

              <Step n={8} title="Önceliklendirilmiş Yol Haritası Sunulur">
                <p>
                  Her eksik aynı derecede önemli değildir. uSeen, yapılacak işleri önceliklendirir.
                </p>
                <RoadmapBlock
                  label="Acil yapılması gerekenler"
                  tone="urgent"
                  items={[
                    "İşletme bilgilerindeki temel eksikleri düzeltmek",
                    "Ana hizmetleri web sitesinde netleştirmek",
                    "Konum ve iletişim bilgilerini görünür hale getirmek",
                    "AI arama niyetlerinde eksik kalan alanları güçlendirmek",
                  ]}
                />
                <RoadmapBlock
                  label="Orta vadede yapılması gerekenler"
                  tone="mid"
                  items={[
                    "Soru-cevap içerikleri oluşturmak",
                    "Hizmet sayfalarını geliştirmek",
                    "Rakiplerden eksik kalan açıklamaları tamamlamak",
                    "Google işletme açıklamasını iyileştirmek",
                  ]}
                />
                <RoadmapBlock
                  label="Uzun vadede yapılması gerekenler"
                  tone="long"
                  items={[
                    "Marka otoritesini artırmak",
                    "Dış kaynaklarda görünür olmak",
                    "Düzenli içerik üretmek",
                    "Yorum ve güven sinyallerini güçlendirmek",
                  ]}
                />
                <p className="text-sm text-slate-400">
                  Bu yapı sayesinde işletmeler nereden başlayacağını bilir.
                </p>
              </Step>
            </Section>

            <Section icon={<Users size={18} />} title="uSeen Hangi İşletmeler İçin Uygundur?">
              <p>uSeen özellikle yerel işletmeler ve hizmet odaklı markalar için uygundur.</p>
              <div className="not-prose grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                {[
                  "Restoranlar", "Kafeler", "Oteller",
                  "Kamp alanları", "Klinikler", "Güzellik merkezleri",
                  "Diş hekimleri", "Oto servisleri", "Emlak ofisleri",
                  "Özel okullar", "Spor salonları", "Turizm işletmeleri",
                ].map((label) => (
                  <div key={label} className="rounded-lg border border-white/5 bg-black/30 px-3 py-2 text-sm text-slate-300">
                    {label}
                  </div>
                ))}
              </div>
              <p>
                Ancak <strong className="text-white">yapay zekâlarda görünür olmak isteyen her işletme </strong>
                için kullanılabilir.
              </p>
            </Section>

            <Section icon={<TrendingUp size={18} />} title="uSeen Neden Önemlidir?">
              <p>Çünkü kullanıcıların karar alma biçimi değişiyor.</p>
              <p>
                Eskiden kullanıcılar Google'da arama yapar, çıkan sonuçları inceler ve karar verirdi.
                Artık birçok kullanıcı doğrudan yapay zekâlara soruyor:
              </p>
              <BulletList items={[
                "Bana en iyi seçeneği öner.",
                "Bu bölgede güvenilir olan kim?",
                "Hangi firmayı tercih etmeliyim?",
                "Benim ihtiyacıma en uygun işletme hangisi?",
              ]} />
              <p>
                Bu sorulara verilen cevaplarda yer almak, yeni dönemin en değerli dijital görünürlük
                alanlarından biri olacak. uSeen, işletmenizin bu yeni döneme hazır olup olmadığını gösterir.
              </p>
              <p className="text-sm text-slate-400">
                Detaylı bilgi:{" "}
                <Link to="/ai-visibility-nedir" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2" data-testid="nasil-internal-ai-visibility">
                  AI Visibility Nedir?
                </Link>{" "}
                ·{" "}
                <Link to="/geo-nedir" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2" data-testid="nasil-internal-geo">
                  GEO Nedir?
                </Link>
              </p>
            </Section>

            <Section icon={<Trophy size={18} />} title="uSeen'in Farkı Nedir?">
              <p>
                uSeen, klasik SEO araçlarından farklı olarak yalnızca anahtar kelime sıralamasına
                odaklanmaz. Daha geniş bir soruya cevap arar:
              </p>
              <div className="not-prose my-5 rounded-2xl border border-cyan-500/30 bg-cyan-500/10 px-5 py-4 text-white text-lg leading-relaxed">
                "Yapay zekâlar bu işletmeyi anlıyor mu, güvenilir buluyor mu ve önerilere hazır
                görüyor mu?"
              </div>
              <p>Bu nedenle uSeen'in yaklaşımı daha bütünseldir. Birlikte değerlendirdiği alanlar:</p>
              <div className="not-prose grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                {[
                  { label: "SEO", icon: <Search size={12} /> },
                  { label: "GEO", icon: <Compass size={12} /> },
                  { label: "AI Visibility", icon: <Bot size={12} /> },
                  { label: "Niyet kapsamı", icon: <Target size={12} /> },
                  { label: "İçerik kalitesi", icon: <FileText size={12} /> },
                  { label: "GBP hazırlığı", icon: <CheckCircle2 size={12} /> },
                  { label: "Güven sinyalleri", icon: <Trophy size={12} /> },
                  { label: "Dijital tutarlılık", icon: <Layers size={12} /> },
                  { label: "Rakip karşılaştırması", icon: <Users size={12} /> },
                  { label: "Görev bazlı iyileştirme", icon: <ListChecks size={12} /> },
                ].map((it) => (
                  <div key={it.label} className="flex items-center gap-2 rounded-lg border border-white/5 bg-black/30 px-3 py-2 text-sm text-slate-300">
                    <span className="text-cyan-300">{it.icon}</span> {it.label}
                  </div>
                ))}
              </div>
              <p>
                Bu alanları birlikte değerlendirir ve işletmeye uygulanabilir bir yol haritası sunar.
              </p>
            </Section>

            <Section icon={<Rocket size={18} />} title="Sonuç">
              <p>
                uSeen, işletmenizin yapay zekâ çağındaki görünürlüğünü ölçmek ve artırmak için
                geliştirilmiş bir platformdur.
              </p>
              <p>
                Sadece bugünün Google sonuçlarına değil, yapay zekâ destekli arama dünyasına
                hazırlanmanıza yardımcı olur.
              </p>
              <p>
                İşletmenizin internette nasıl göründüğünü, yapay zekâlar tarafından nasıl anlaşıldığını
                ve hangi eksikler nedeniyle önerilerde geri kalabileceğini gösterir.
              </p>
              <p>Sonrasında bu eksikleri net, uygulanabilir görevlere dönüştürür.</p>
              <p className="text-white font-medium">
                Bugünün arama motorları artık yapay zekalar. Sen de önerilerde öne çık, görünür ol.
              </p>
            </Section>
          </Article>

          {/* CTA */}
          <div
            className="mt-12 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-7 sm:p-10 relative overflow-hidden"
            data-testid="nasil-cta-block"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                AI görünürlük skorunuzu ölçmeye başlayın
              </h3>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-6">
                İşletmenizin yapay zekâ aramalarında hangi müşteri niyetlerinde güçlü, hangi alanlarda
                eksik olduğunu görmek için ücretsiz kontrol ile başlayın.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/kayit"
                  className="neon-btn rounded-xl px-5 py-3 text-white font-semibold inline-flex items-center gap-2"
                  data-testid="nasil-cta-primary"
                >
                  Ücretsiz Başla <ArrowRight size={14} />
                </Link>
                <Link
                  to="/ai-visibility-nedir"
                  className="ghost-btn rounded-xl px-5 py-3 text-white border border-white/10 inline-flex items-center gap-2"
                  data-testid="nasil-cta-secondary"
                >
                  AI Visibility Nedir?
                </Link>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <section className="mt-16" data-testid="nasil-faq">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">
              <HelpCircle size={11} /> Sık Sorulan Sorular
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">
              uSeen hakkında merak edilenler
            </h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-white/5 bg-black/30 px-5 py-4 open:bg-white/[0.03] transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-medium">
                    <span>{f.q}</span>
                    <span className="text-cyan-300 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-400 text-center">
              Sorularınız mı var?{" "}
              <Link to="/iletisim" className="text-cyan-300 hover:text-cyan-200" data-testid="nasil-contact-link">
                Bizimle iletişime geçin
              </Link>
              .
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function Article({ children }) {
  return <article className="space-y-10 text-slate-300 leading-relaxed">{children}</article>;
}

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-white mb-3 flex items-center gap-2.5">
        {icon && <span className="text-cyan-300">{icon}</span>}
        {title}
      </h2>
      <div className="space-y-3 text-[0.975rem]">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Numbered H3 step block, used inside the "Çalışma Süreci" section. */
function Step({ n, title, children }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-black/20 p-5 sm:p-6 my-4">
      <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-2 flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-xs font-bold shrink-0">
          {n}
        </span>
        {title}
      </h3>
      <div className="text-slate-300 text-[0.95rem] leading-relaxed pl-[2.4rem] space-y-2">
        {children}
      </div>
    </div>
  );
}

function RoadmapBlock({ label, tone, items }) {
  const toneMap = {
    urgent: "border-rose-500/25 bg-rose-500/5 text-rose-300",
    mid: "border-amber-500/25 bg-amber-500/5 text-amber-300",
    long: "border-emerald-500/25 bg-emerald-500/5 text-emerald-300",
  };
  return (
    <div className={`not-prose my-4 rounded-xl border px-4 py-3 ${toneMap[tone] || ""}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2 opacity-90">{label}</div>
      <ul className="space-y-1.5 text-slate-300 text-[0.92rem]">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="opacity-80">›</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
